export function MaskedSecretDirective($timeout) {
    'ngInject'
    const directive = {
        require: "ngModel",
        restrict: "A",
        link: link,
        scope: {
            mask: "=",
            promptChar: "=?",
            reveal: "=?"
        }
    };
    const placeholderChars = /[09#L?&CAa]/;

    return directive;

    function link(scope, el, attrs, ngModel) {
        scope.promptChar = scope.promptChar || "_"; // Default prompt character.
        scope.reveal = scope.reveal || function (model, char, i) {
            return false;
        };
        scope.secretChar = "●";
        /* The parsing process goes like this:
         * Eg, with a view value of: ●●●-4_-____
         *            model value of: 123
         *            mask of: 000-00-0000
         *            prompt char: _
         * 1. Fill secret characters ● with model characters.
         *    Return
         *    123-4_-____
         * 2. Match unmasked value with the character restrictions and strip out placeholder characters from mask.
         *    If there is an non-matching character, return the previous model value. Only necessary if a mask is used.
         *    Return:
         *    1234
         * 3. Fill mask with the secret character in the place of model value characters.
         *    Set element text to this transformed mask:
         *    ●●●-●_-____
         *    Return:
         *    1234
         */

        // Prevent attempts to autocomplete to the placeholder values.
        el.attr("autocomplete", "off");
        el.on("keydown", blockInvalidInput);
        el.on("focus", function (e) {
            // Display placeholder on focus, in case it was hidden.
            $timeout(function () {
                setInputValue(ngModel.$modelValue);
                // Select all text on focus.
                el.prop("selectionStart", 0);
                el.prop("selectionEnd", el.val().length);
            });
        });
        el.on("blur", function (e) {
            // Hide placeholder if it is blank.
            $timeout(function () {
                if ((ngModel.$modelValue || "") === "") {
                    el.val("");
                } else {
                    // For some reason, viewValue doesn't get the updated value until after blur.
                    const maskedValue = formatModelIntoMask(conformToMask(unmaskSecret(ngModel.$modelValue)));
                    ngModel.$setValidity("unfinished", maskedValue.indexOf(scope.promptChar) < 0);
                }
            });
        });


        ngModel.$parsers.push(unmaskSecret);
        ngModel.$parsers.push(conformToMask);
        ngModel.$parsers.push(function (viewValue) {
            setInputValue(viewValue);
            return viewValue;
        });

        ngModel.$formatters.push(formatModelIntoMask);

        ngModel.$render = function () {
            // Display the value in input (doesn't happen automatically)
            if ((ngModel.$modelValue || "") === "") {
                el.val("");
            } else {
                el.val(ngModel.$viewValue);
            }
        }

        function unmaskSecret(viewValue) {
            viewValue = viewValue || "";

            // Previous model value.
            const actualValue = ngModel.$modelValue || "";
            let unmaskedValue = viewValue;
            for (let i = 0; i < actualValue.length && i < viewValue.length; i++) {
                // Replace the masked characters with their original values.
                unmaskedValue = unmaskedValue.replace(scope.secretChar, actualValue[i]);
            }
            return unmaskedValue;
        }

        /**
         * Strip characters away from the view value that do not conform to the mask,
         * if any.
         * @param {any} viewValue The model's view value.
         * @returns {any} The view value conformed to a mask.
         */
        function conformToMask(viewValue) {
            viewValue = viewValue || "";

            let actualValue = viewValue;
            if (scope.mask) {
                // Match the text against the mask.
                actualValue = "";
                let j = 0;
                for (let i = 0; i < scope.mask.length; i++) {
                    const maskChar = scope.mask[i];
                    if (!isMaskCharacterPlaceholder(maskChar)) {
                        continue;
                    }

                    let added = false;
                    // Find the next view value character that matches the mask.
                    do {
                        const textChar = viewValue[j] || scope.promptChar;
                        if (textChar !== scope.promptChar) {
                            if (isCharacterAllowedByMask(maskChar, textChar, scope.promptChar)) {
                                actualValue += textChar;
                                added = true;
                            }
                        }
                    } while (j++ < viewValue.length && !added);
                }
            }

            return actualValue;
        }

        function formatModelIntoMask(modelValue) {
            let res = "";
            modelValue = modelValue || "";

            // If a mask is used, replace all characters with the secret character.
            // Otherwise, only replace non-prompt characters in the placeholder slot.
            if (!scope.mask) {
                for (let i = 0; i < modelValue.length; i++) {
                    if (scope.reveal(modelValue, modelValue[i], i)) {
                        res += modelValue[i];
                    } else {
                        res += scope.secretChar;
                    }
                }
            } else {
                let j = 0;
                for (let i = 0; i < scope.mask.length; i++) {
                    const maskChar = scope.mask[i];
                    let currentTextChar;
                    if (j < modelValue.length) {
                        currentTextChar = scope.reveal(modelValue, modelValue[j], j) ? modelValue[j] : scope.secretChar;
                    } else {
                        currentTextChar = scope.promptChar;
                    }
                    if (isMaskCharacterPlaceholder(maskChar)) {
                        res += currentTextChar;
                        j++;
                    } else {
                        res += maskChar;
                    }
                }
            }
            return res;
        }

        /**
         * Update the element to display the formatted value.
         * @param {any} value The input value.
         */
        function setInputValue(value) {
            const maskedValue = formatModelIntoMask(value);

            // Replacing the element text with the new text will change the cursor position,
            // so calculate the new selection.
            let cursorPosition = maskedValue.indexOf(scope.promptChar);
            cursorPosition = cursorPosition < 0 ? maskedValue.length : cursorPosition;

            if (scope.mask) {
                const leftHalf = maskedValue.substring(0, cursorPosition);
                const remainingMask = scope.mask.substring(leftHalf.length);
                const nextPlaceholder = placeholderChars.exec(remainingMask);
                if (nextPlaceholder !== null) {
                    cursorPosition = nextPlaceholder.index + leftHalf.length;
                }
            }

            ngModel.$setViewValue(maskedValue);
            el.val(maskedValue);
            el.prop("selectionStart", cursorPosition);
            el.prop("selectionEnd", cursorPosition);
        }

        function blockInvalidInput(e) {
            // Cancel processing on keyboard shortcuts.
            if (scope.mask === undefined || e.ctrlKey || e.altKey) return;

            const currentViewValue = el.val();

            // Only allow delete and backspace if they delete a filled placeholder character.
            // If they delete a prompt char or static char, only move the cursor backward or forward.
            if (e.keyCode === 8) {
                // Backspace - find last instance of a placeholder

                handleBackspaceInput(e);
                return;
            } else if (e.keyCode === 46) {
                // Delete - find next instance of a placeholder
                handleDeleteInput(e);
                return;
            }

            // Ignore non-inputtable keys.
            if (!e.key || e.key.length > 1) return;

            const textChar = e.key;

            // Move cursor before the last empty placeholder.
            let lastEmptyPrompt = -1;
            for (let i = currentViewValue.length - 1; i >= 0; --i) {
                if (currentViewValue[i] === scope.promptChar) {
                    lastEmptyPrompt = i;
                } else if (currentViewValue[i] === scope.secretChar) {
                    break;
                }
            }
            if (lastEmptyPrompt >= 0 && el.prop("selectionStart") > lastEmptyPrompt) {
                el.prop("selectionStart", lastEmptyPrompt);
            }

            // Remove the text after the cursor position: it will be replaced by (or depending on) the key being pressed.
            const leftHalf = currentViewValue.substring(0, el.prop("selectionStart"));
            const unfilledPart = scope.mask.substring(leftHalf.length) || "";
            const nextPlaceholder = placeholderChars.exec(unfilledPart);
            if (nextPlaceholder !== null) {
                const maskChar = unfilledPart[nextPlaceholder.index];
                if (isCharacterAllowedByMask(maskChar, textChar, scope.promptChar)) {
                    // Allow keydown event so the character can be inputted.
                    // Technically, the rest of the input's value could be invalid at this point.
                    return;
                }
            }
            // Prevent entry; cursor is at end of mask or placeholder does not allow this character.
            e.preventDefault();
            e.stopPropagation();
            return false;
        }

        function handleBackspaceInput(e) {
            const currentViewValue = el.val();
            const filledMask = scope.mask.substring(0, el.prop("selectionStart"));
            for (let i = filledMask.length - 1; i >= 0; i--) {
                if (isMaskCharacterPlaceholder(filledMask[i])) {
                    el.prop("selectionStart", i === filledMask.length - 1 ? i : i + 1);
                    break;
                }
            }
            let anyDeletedFilledPlaceholders = false;
            for (let i = el.prop("selectionStart"); i < el.prop("selectionEnd"); i++) {
                if (currentViewValue[i] !== scope.promptChar && isMaskCharacterPlaceholder(scope.mask[i])) {
                    // Some actual characters would be deleted, so allow the key press to success.
                    anyDeletedFilledPlaceholders = true;
                    break;
                }
            }
            if (!anyDeletedFilledPlaceholders) {
                // Finish moving cursor over.
                e.preventDefault();
                e.stopPropagation();
                el.prop("selectionEnd", el.prop("selectionStart"));
            }
        }

        function handleDeleteInput(e) {
            // Cancel delete if there are no more filled inputs to the right
            const currentViewValue = el.val();
            let anyDeletedFilledPlaceholders = false;
            for (let i = el.prop("selectionStart"); i < currentViewValue.length; i++) {
                if (currentViewValue[i] !== scope.promptChar && isMaskCharacterPlaceholder(scope.mask[i])) {
                    // Some actual characters would be deleted, so allow the key press to success.
                    anyDeletedFilledPlaceholders = true;
                    break;
                }
            }
            if (!anyDeletedFilledPlaceholders) {
                // Finish moving cursor over.
                e.preventDefault();
                e.stopPropagation();
            }
        }
    }

    function isMaskCharacterPlaceholder(maskChar) {
        return placeholderChars.test(maskChar)
    }

    function isCharacterAllowedByMask(maskChar, textChar, promptChar) {
        // Prompt character is always accepted.
        if (textChar === promptChar) return true;

        switch (maskChar) {
            case "0":
                // Allow 0-9.
                return /[0-9]/.test(textChar);
            case "9":
                // Allow 0-9 and spaces
                return /[0-9\s]/.test(textChar);
            case "#":
                // Allow 0-9, spaces, and +/-
                return /[0-9\s+-]/.test(textChar);
            case "L":
                // Allow alphabetical characters.
                return /[a-zA-Z]/.test(textChar);
            case "?":
                // Accept alphabetical characters and spaces.
                return /[a-zA-Z\s]/.test(textChar);
            case "&":
                // Accept any character.
                return /^[^\s]*$/.test(textChar);
            case "C":
                // Accept any character or space.
                return true;
            case "A":
                // Accept alphanumeric.
                return /[a-zA-Z0-9]/.test(textChar);
            case "a":
                // Accept alphanumeric and spaces.
                return /[a-zA-Z0-9\s]/.test(textChar);
            default:
                return true;
        }
    }
}
